import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Input } from 'antd';
import { colors } from '../../design-system/tokens/colors';
import { XIcon, PaperAirplaneIcon } from '@heroicons/react/outline';
import type { Creator } from '../Creators/creatorTypes';
import type { TableColumn, TableRow, OutreachCellData } from './types';
import { extractVariablesFromCreator, substituteVariables, convertPlainTextToHtml, type PersonalizedEmailPreview } from './utils/variableSubstitution';

const { TextArea } = Input;

interface OutreachPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  column: TableColumn;
  creator: Creator;
  rowData: TableRow['data'];
  allColumns: TableColumn[];
  outreachData?: OutreachCellData;
  onSend: (preview: PersonalizedEmailPreview) => void;
  isSending?: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 780px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.2s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.border.secondary};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    width: 20px;
    height: 20px;
    color: #635bff;
    transform: rotate(90deg);
  }
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.text.primary};
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${colors.text.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.12s ease;
  
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.04);
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${colors.text.primary};
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  border: 1px solid ${colors.border.secondary};
`;

const EmailContent = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${colors.text.primary};
  padding: 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  border: 1px solid ${colors.border.secondary};
  white-space: pre-wrap;
  word-break: break-word;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.border.secondary};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.12s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.$variant === 'primary' ? `
    background: #635bff;
    color: white;
    
    &:hover:not(:disabled) {
      background: #5249e6;
    }
    
    &:active:not(:disabled) {
      background: #4037d9;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: transparent;
    color: ${colors.text.secondary};
    
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.04);
      color: ${colors.text.primary};
    }
    
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  `}
  
  svg {
    width: 16px;
    height: 16px;
    transform: rotate(90deg);
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 12px;
  ${props => props.$type === 'success' ? `
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
  ` : `
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  `}
`;

export const OutreachPreviewModal: React.FC<OutreachPreviewModalProps> = ({
  isOpen,
  onClose,
  column,
  creator,
  rowData,
  allColumns,
  outreachData,
  onSend,
  isSending = false
}) => {
  const [previewSubject, setPreviewSubject] = useState<string>('');
  const [previewMessage, setPreviewMessage] = useState<string>('');

  useEffect(() => {
    if (isOpen && column.outreach_config) {
      // Extract variables
      const variables = extractVariablesFromCreator(creator, rowData, allColumns);
      
      // Substitute in template and subject
      const substitutedSubject = column.outreach_config.subject 
        ? substituteVariables(column.outreach_config.subject, variables) 
        : '';
      const substitutedMessage = substituteVariables(column.outreach_config.template, variables);
      
      setPreviewSubject(substitutedSubject);
      setPreviewMessage(substitutedMessage);
    }
  }, [isOpen, column, creator, rowData, allColumns]);

  if (!isOpen) return null;

  const creatorEmail = creator.contacts?.find(c => c.type === 'email')?.value;
  const canSend = creatorEmail && !isSending;
  const isSent = outreachData?.status === 'sent';

  return (
    <Overlay $isOpen={isOpen} onClick={isSending ? undefined : onClose}>
      <Modal $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <PaperAirplaneIcon />
            <h3>Email Preview</h3>
          </Title>
          <CloseButton onClick={onClose} disabled={isSending}>
            <XIcon />
          </CloseButton>
        </Header>

        <Content>
          <Section>
            <Label>To</Label>
            <Value>{creatorEmail || 'No email address found'}</Value>
          </Section>

          <Section>
            <Label>Subject</Label>
            <Input
              value={previewSubject}
              onChange={(e) => setPreviewSubject(e.target.value)}
              placeholder="Email subject"
              disabled={isSending}
              style={{
                fontSize: '14px',
                padding: '12px',
                borderRadius: '6px',
                border: `1px solid ${colors.border.secondary}`,
                background: 'rgba(0, 0, 0, 0.02)'
              }}
            />
          </Section>

          <Section>
            <Label>Message</Label>
            <TextArea
              value={previewMessage}
              onChange={(e) => setPreviewMessage(e.target.value)}
              placeholder="Email message"
              disabled={isSending}
              rows={12}
              style={{
                fontSize: '13px',
                lineHeight: '1.6',
                padding: '16px',
                borderRadius: '6px',
                border: `1px solid ${colors.border.secondary}`,
                background: 'rgba(0, 0, 0, 0.02)',
                resize: 'vertical',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
            />
          </Section>

          {isSent && !isSending && (
            <StatusMessage $type="success">
              ✓ Email sent successfully{outreachData.sentAt ? ` on ${new Date(outreachData.sentAt).toLocaleString()}` : ''}
            </StatusMessage>
          )}

          {outreachData?.status === 'failed' && outreachData?.error && (
            <StatusMessage $type="error">
              ✗ Failed to send: {outreachData.error}
            </StatusMessage>
          )}
        </Content>

        <Footer>
          <Button $variant="secondary" onClick={onClose} disabled={isSending}>
            Close
          </Button>
          {canSend && (
            <Button 
              $variant="primary" 
              onClick={() => onSend({ 
                html: convertPlainTextToHtml(previewMessage), 
                subject: previewSubject, 
                plainText: previewMessage 
              })} 
              disabled={isSending}
            >
              <PaperAirplaneIcon />
              {isSending ? 'Sending...' : (isSent ? 'Resend Email' : 'Send Email')}
            </Button>
          )}
        </Footer>
      </Modal>
    </Overlay>
  );
};

